/**
 * Rendu d'un dock (porteur allié, baie de la station) : modèle dédié ou mêmes boîtes que la collision, tunnel
 * éclairé, anneaux de guidage d'appontage dont la couleur indique si les conditions sont réunies.
 * La baie de la station est reliée à la station (décor lointain) par un pylône balisé.
 */
import { Color3, MeshBuilder, PBRMaterial, Quaternion, StandardMaterial, TransformNode, Vector3, type Mesh, type Scene } from '@babylonjs/core';
import type { ArtData, DockData, DockId } from '../data';
import type { DockReadiness } from '../sim/encounter/dock';
import type { StationArm } from '../sim/world/station';
import type { ModelLibrary } from './models';
import { SurfaceDetailPlugin, type SurfaceDetailSet } from './surfaceDetail';

const READY = new Color3(0.2, 1, 0.45);
const PARTIAL = new Color3(1, 0.7, 0.1);
const IDLE = new Color3(0.1, 0.65, 1);

export class DockView {
  readonly root: TransformNode;
  private readonly rings: Mesh[] = [];
  private readonly ringMaterial: StandardMaterial;
  private readonly navLights: Mesh[] = [];
  private time = 0;

  constructor(
    scene: Scene,
    readonly id: DockId,
    private readonly data: DockData,
    art: ArtData,
    models: ModelLibrary,
    private readonly detail: SurfaceDetailSet,
    arm?: StationArm | null,
  ) {
    this.root = new TransformNode(`dock-${id}`, scene);
    this.root.position.set(...data.position);
    this.root.rotationQuaternion = new Quaternion(...data.rotation);
    // Modèles dédiés (tools/assets/carrier_model.py, station_bay_model.py) s'ils existent ; sinon silhouette en
    // blocs = boîtes de collision.
    const modelKey = id === 'carrier' ? 'carrier' : 'stationBay';
    const hasModel = models.has(modelKey);

    if (hasModel) {
      const model = models.instantiate(modelKey, `${id}-model`);
      model.parent = this.root;
      // Parois du tunnel vues de l'intérieur : faces rendues des deux côtés.
      // Le ciel n'est pas préfiltré : sans réduction, les parois mates le reflètent comme des miroirs.
      for (const mesh of model.getChildMeshes()) {
        const material = mesh.material;
        if (!material) continue;
        material.backFaceCulling = false;
        // Modèle cuit (vigie_dock_model.py) : textures complètes, reflets du ciel atténués comme ailleurs.
        if (material instanceof PBRMaterial && material.albedoTexture) material.environmentIntensity = 0.3;
        // Modèle texturé : sa texture suffit, pas de détail de surface superposé.
        if (material instanceof PBRMaterial && !material.name.endsWith('glow') && !material.albedoTexture) {
          material.environmentIntensity = 0.12;
          // Coque : texture de panneaux affirmée ; accents sombres et peinture : surtout le relief.
          const name = material.name;
          this.addDetail(material, art, name.includes('trim') ? 0.35 : name.includes('paint') ? 0.15 : 1);
        }
      }
    } else {
      this.buildBlocks(scene, data, art);
    }
    if (arm) this.buildArm(scene, arm);

    const glow = (name: string, color: Color3) => {
      const m = new StandardMaterial(name, scene);
      m.disableLighting = true;
      m.emissiveColor = color;
      return m;
    };
    // Bandes lumineuses le long du tunnel et plot d'appontage.
    const t = data.tunnel;
    const accent = art.liveries[id].accent;
    const stripMat = glow(`${id}-strip`, new Color3(accent[0] * 0.5, accent[1] * 0.5, accent[2] * 0.5));
    // Le modèle dédié porte ses propres lumières de tunnel.
    for (const side of hasModel ? [] : [-1, 1]) {
      for (const y of [-t.halfHeight + 0.3, t.halfHeight - 0.3]) {
        const strip = MeshBuilder.CreateBox('carrier-strip', { width: 0.3, height: 0.25, depth: t.halfLength * 2 }, scene);
        strip.position.set(side * (t.halfWidth - 0.4), y, 0);
        strip.parent = this.root;
        strip.material = stripMat;
        strip.isPickable = false;
      }
    }
    // Marquage de plot : seulement sans modèle dédié (il porte le sien).
    const pad = MeshBuilder.CreateTorus('carrier-pad', { diameter: 14, thickness: 0.5, tessellation: 40 }, scene);
    pad.setEnabled(!hasModel);
    pad.position.set(data.pad[0]!, -t.halfHeight + 0.4, data.pad[2]!);
    pad.parent = this.root;
    pad.material = glow('carrier-pad', new Color3(0.2, 1, 0.5));
    pad.isPickable = false;

    // Anneaux de guidage derrière la poupe.
    this.ringMaterial = glow(`${id}-rings`, IDLE.clone());
    this.ringMaterial.alpha = 0.75;
    for (let i = 0; i < 4; i++) {
      const ring = MeshBuilder.CreateTorus(`${id}-ring-${i}`, { diameter: Math.max(t.halfWidth, t.halfHeight) * 2 - 2 - i * 2, thickness: 0.7, tessellation: 48 }, scene);
      ring.rotation.x = Math.PI / 2;
      ring.position.set(data.pad[0]!, data.pad[1]!, t.halfLength + 18 + i * 40);
      ring.parent = this.root;
      ring.material = this.ringMaterial;
      ring.isPickable = false;
      this.rings.push(ring);
    }

    // Feux de navigation : rouge à bâbord, vert à tribord, aux deux extrémités.
    const navColors: [number, Color3][] = [[-1, new Color3(1, 0.1, 0.05)], [1, new Color3(0.1, 1, 0.3)]];
    const halfWidth = Math.max(...data.parts.map((p) => Math.abs(p.center[0]!) + p.half[0]!));
    for (const [side, color] of navColors) {
      for (const z of [-t.halfLength + 10, t.halfLength - 10]) {
        const light = MeshBuilder.CreateSphere(`${id}-nav`, { diameter: 2.6, segments: 8 }, scene);
        light.position.set(side * (halfWidth + 1), 0, z);
        light.parent = this.root;
        light.material = glow(`${id}-nav-${side}`, color);
        light.isPickable = false;
        this.navLights.push(light);
      }
    }
  }

  /** Détail de surface triplanaire dans le repère du dock (voir surfaceDetail.ts). */
  private addDetail(material: PBRMaterial, art: ArtData, albedoScale: number): void {
    if (material.pluginManager?.getPlugin('SurfaceDetail')) return;
    const d = art.dockDetail;
    const plugin = new SurfaceDetailPlugin(material, this.detail, { hullTile: d.hullTile, hangarTile: d.hangarTile, albedo: d.albedo * albedoScale, normal: d.normal });
    this.root.computeWorldMatrix(true).invertToRef(plugin.invWorld);
    const t = this.data.tunnel;
    // Volume « hangar » : le tunnel, ses parois et ses plafonds (épaisseur des blocs comprise).
    plugin.hangar.set(t.halfWidth + d.wallMargin, t.halfHeight + d.wallMargin, t.halfLength + 0.5);
  }

  /** Silhouette en blocs : acier teinté par la couleur de base de la livrée, détail de surface triplanaire. */
  private buildBlocks(scene: Scene, data: DockData, art: ArtData): void {
    const livery = art.liveries[this.id];
    const hull = new PBRMaterial(`${this.id}-hull`, scene);
    hull.albedoColor = new Color3(...livery.base);
    hull.metallic = 0.55;
    hull.roughness = 0.55;
    // Le ciel n'est pas préfiltré : sans réduction, les parois mates le reflètent comme des miroirs.
    hull.environmentIntensity = 0.15;
    hull.maxSimultaneousLights = 8;
    this.addDetail(hull, art, 1);
    for (const part of data.parts) {
      const box = MeshBuilder.CreateBox(`${this.id}-${part.id}`, { width: part.half[0]! * 2, height: part.half[1]! * 2, depth: part.half[2]! * 2 }, scene);
      box.position.set(...part.center);
      box.parent = this.root;
      box.material = hull;
      box.isPickable = false;
    }
    this.buildGreebles(scene, data, art);
  }

  /**
   * Habillage de la silhouette en blocs (visuel seul, saillies ≤ 2 m hors des boîtes de collision) : cadres des
   * bouches du tunnel avec liseré lumineux, nervures sur les flancs, mâts d'antenne balisés.
   */
  private buildGreebles(scene: Scene, data: DockData, art: ArtData): void {
    const trim = new PBRMaterial(`${this.id}-trim`, scene);
    trim.albedoColor = new Color3(0.08, 0.085, 0.1);
    trim.metallic = 0.7;
    trim.roughness = 0.45;
    trim.environmentIntensity = 0.15;
    trim.maxSimultaneousLights = 8;
    this.addDetail(trim, art, 0.3);
    const accent = art.liveries[this.id].accent;
    const glowMat = new StandardMaterial(`${this.id}-greeble-glow`, scene);
    glowMat.disableLighting = true;
    glowMat.emissiveColor = new Color3(...accent);
    const add = (name: string, size: [number, number, number], at: [number, number, number], material: PBRMaterial | StandardMaterial) => {
      const box = MeshBuilder.CreateBox(`${this.id}-${name}`, { width: size[0], height: size[1], depth: size[2] }, scene);
      box.position.set(...at);
      box.parent = this.root;
      box.material = material;
      box.isPickable = false;
      return box;
    };
    const t = data.tunnel;
    const frame = 2.5;
    for (const end of [-1, 1]) {
      const z = end * (t.halfLength + 1);
      // Cadre autour de la bouche, puis liseré lumineux juste à l'intérieur.
      add(`frame-top-${end}`, [2 * (t.halfWidth + frame), frame, 2], [0, t.halfHeight + frame / 2, z], trim);
      add(`frame-bottom-${end}`, [2 * (t.halfWidth + frame), frame, 2], [0, -t.halfHeight - frame / 2, z], trim);
      for (const side of [-1, 1]) add(`frame-side-${end}-${side}`, [frame, 2 * t.halfHeight, 2], [side * (t.halfWidth + frame / 2), 0, z], trim);
      const zg = end * (t.halfLength + 2.05);
      add(`glow-top-${end}`, [2 * t.halfWidth, 0.35, 0.1], [0, t.halfHeight + 0.4, zg], glowMat);
      add(`glow-bottom-${end}`, [2 * t.halfWidth, 0.35, 0.1], [0, -t.halfHeight - 0.4, zg], glowMat);
      for (const side of [-1, 1]) add(`glow-side-${end}-${side}`, [0.35, 2 * t.halfHeight, 0.1], [side * (t.halfWidth + 0.4), 0, zg], glowMat);
    }
    // Nervures sur les flancs extérieurs.
    // Corps principal : parties qui couvrent toute la longueur du tunnel (plancher, toit, flancs).
    const body = data.parts.filter((p) => p.half[2]! >= t.halfLength - 0.5);
    const outer = Math.max(...body.map((p) => Math.abs(p.center[0]!) + p.half[0]!));
    const bottom = Math.min(...body.map((p) => p.center[1]! - p.half[1]!));
    const top = Math.max(...body.map((p) => p.center[1]! + p.half[1]!));
    for (let z = -t.halfLength + 10; z <= t.halfLength - 10; z += 20) {
      for (const side of [-1, 1]) add(`rib-${z}-${side}`, [1.6, top - bottom - 1, 2.4], [side * (outer + 0.8), (top + bottom) / 2, z], trim);
    }
    // Mâts d'antenne sur le point le plus haut, feux clignotants (balisage).
    const crest = data.parts.reduce((a, p) => (p.center[1]! + p.half[1]! > a.center[1]! + a.half[1]! ? p : a));
    const crestTop = crest.center[1]! + crest.half[1]!;
    const beacon = new StandardMaterial(`${this.id}-mast-light`, scene);
    beacon.disableLighting = true;
    beacon.emissiveColor = new Color3(1, 0.15, 0.08);
    [-0.6, 0, 0.6].forEach((f, i) => {
      const height = 8 + i * 4;
      const z = crest.center[2]! + f * crest.half[2]!;
      const mast = MeshBuilder.CreateCylinder(`${this.id}-mast-${i}`, { height, diameter: 0.7, tessellation: 8 }, scene);
      mast.position.set(crest.center[0]! + (i - 1) * 3, crestTop + height / 2, z);
      mast.parent = this.root;
      mast.material = trim;
      mast.isPickable = false;
      const light = MeshBuilder.CreateSphere(`${this.id}-mast-light-${i}`, { diameter: 1.4, segments: 6 }, scene);
      light.position.set(mast.position.x, crestTop + height + 0.6, z);
      light.parent = this.root;
      light.material = beacon;
      light.isPickable = false;
      this.navLights.push(light);
    });
  }

  /**
   * Bras télescopique de la baie vers le collier du moyeu de la station : trois sections emboîtées de diamètre
   * décroissant, colliers sombres aux jonctions, bande orange, feux balisés. Collision : boîte du bras (sim).
   */
  private buildArm(scene: Scene, arm: StationArm): void {
    const start = new Vector3(arm.from.x, arm.from.y, arm.from.z);
    const end = new Vector3(arm.to.x, arm.to.y, arm.to.z);
    const length = Vector3.Distance(start, end);
    const dir = end.subtract(start).normalize();
    const metal = new PBRMaterial(`${this.id}-arm`, scene);
    metal.albedoColor = new Color3(0.58, 0.6, 0.63);
    metal.metallic = 0.55;
    metal.roughness = 0.5;
    metal.environmentIntensity = 0.2;
    metal.maxSimultaneousLights = 8;
    const dark = new PBRMaterial(`${this.id}-arm-trim`, scene);
    dark.albedoColor = new Color3(0.08, 0.085, 0.1);
    dark.metallic = 0.65;
    dark.roughness = 0.45;
    dark.environmentIntensity = 0.15;
    dark.maxSimultaneousLights = 8;
    const paint = new StandardMaterial(`${this.id}-arm-paint`, scene);
    paint.diffuseColor = new Color3(0.9, 0.48, 0.06);
    paint.specularColor = new Color3(0.1, 0.1, 0.1);
    const at = (t: number) => start.add(dir.scale(t * length));
    const tube = (name: string, t0: number, t1: number, radius: number, material: PBRMaterial | StandardMaterial) => {
      const mesh = MeshBuilder.CreateTube(`${this.id}-${name}`, { path: [at(t0), at(t1)], radius, tessellation: 20, cap: 2 }, scene);
      mesh.material = material;
      mesh.isPickable = false;
      return mesh;
    };
    // Sections : la plus large côté station (collier du moyeu), la plus fine côté baie.
    const r = arm.radius;
    tube('arm-0', 0, 0.42, r * 0.62, metal);
    tube('arm-1', 0.36, 0.74, r * 0.8, metal);
    tube('arm-2', 0.68, 1, r, metal);
    for (const [t, radius] of [[0.37, r * 0.72], [0.69, r * 0.9], [0.985, r * 1.18], [0.02, r * 0.85]] as const) {
      tube(`arm-collar-${t}`, t - 0.012, t + 0.012, radius, dark);
    }
    tube('arm-paint', 0.8, 0.83, r * 1.02, paint);
    const beacon = new StandardMaterial(`${this.id}-arm-light`, scene);
    beacon.disableLighting = true;
    beacon.emissiveColor = new Color3(1, 0.65, 0.15);
    const up = Math.abs(dir.y) > 0.9 ? Vector3.Right() : Vector3.Up();
    for (const t of [0.15, 0.5, 0.86]) {
      const light = MeshBuilder.CreateSphere(`${this.id}-arm-beacon`, { diameter: 2.2, segments: 6 }, scene);
      light.position = at(t).add(up.scale(r * (t < 0.36 ? 0.62 : t < 0.7 ? 0.8 : 1) + 0.8));
      light.material = beacon;
      light.isPickable = false;
      this.navLights.push(light);
    }
  }

  update(dt: number, readiness: DockReadiness | null): void {
    this.time += dt;
    const color = !readiness ? IDLE : readiness.inZone && readiness.speedOk && readiness.alignOk ? READY : readiness.inZone ? PARTIAL : IDLE;
    this.ringMaterial.emissiveColor.copyFrom(color);
    // Défilement lumineux vers l'entrée du tunnel.
    this.rings.forEach((ring, i) => {
      ring.visibility = 0.45 + 0.55 * Math.max(0, Math.sin(this.time * 4 - i * 0.9));
    });
    const blink = Math.sin(this.time * 3) > 0.2;
    for (const light of this.navLights) light.setEnabled(blink);
  }

  /** Remet le dock à sa pose de réglage (après une séquence de saut qui l'a déplacé). */
  resetPose(): void {
    this.root.position.set(...this.data.position);
    this.root.rotationQuaternion = new Quaternion(...this.data.rotation);
    this.root.scaling.setAll(1);
  }

  /** Longueur et rayon de la silhouette (portail de saut à sa mesure). */
  extent(): { length: number; radius: number } {
    const z = Math.max(...this.data.parts.map((p) => Math.abs(p.center[2]!) + p.half[2]!));
    const r = Math.max(...this.data.parts.map((p) => Math.hypot(Math.abs(p.center[0]!) + p.half[0]!, Math.abs(p.center[1]!) + p.half[1]!)));
    return { length: z * 2, radius: r };
  }

  /** Position monde du point le plus visible du dock (pour le marqueur HUD). */
  marker(): Vector3 {
    const top = Math.max(...this.data.parts.map((p) => p.center[1]! + p.half[1]!));
    return this.root.position.add(new Vector3(0, top + 6, 0));
  }
}
